import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | OnlineCollegeGuide', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-1998" data-mp="1no9cjus6dxwi">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-1999" data-mp="3mqt1d17w0uvl">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-2000" data-mp="3vs9a74j20oxp" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-2001" data-mp="4ewh5c323cph">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-2002" data-mp="ecavn65f5rf5">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2003" data-mp="nch7knlgyjc7">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-2004" data-mp="2l4bq0cfyw83b">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-2005" data-mp="3ni983gwdtacw">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-2006" data-mp="f99c5lzopx8x" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2007" data-mp="a9wgqcuqzhuq">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-2008" data-mp="t1p7s1q9aszt">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-2009" data-mp="13kzoj556qbp2">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-2010" data-mp="17ls1o9h6h0qm">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-2011" data-mp="jvpbjywtq4un">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-2012" data-mp="2xpiubr2o1xj">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2013" data-mp="3u9r29nqy1c6q">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-2014" data-mp="3l8147z2k3ezz">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-2015" data-mp="3bcpyg694li4d">
              <div className="flex items-start gap-4" data-id="element-2016" data-mp="37am9cetjaf0m">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2017" data-mp="37bxkx0t80r2a">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2018" data-mp="15kkjwmudj3bi">
                    1
                  </span>
                </div>
                <div data-id="element-2019" data-mp="2seo3ttjhejr5">
                  <p className="font-semibold text-gray-800" data-id="element-2020" data-mp="2se4j652g5h9f">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2021" data-mp="3189ilcvnc10k">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2022" data-mp="29hwdj05iv4dl">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2023" data-mp="2dioqa3dlrrem">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2024" data-mp="3oxvgzy472c7i">
                    2
                  </span>
                </div>
                <div data-id="element-2025" data-mp="1tuozkvg1ynfn">
                  <p className="font-semibold text-gray-800" data-id="element-2026" data-mp="1tu5re6g7fdet">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2027" data-mp="1psbwyfwlr5n3">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2028" data-mp="1b4qrwgg62wcj">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2029" data-mp="165mltyojheqf">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2030" data-mp="1g2scx6x5l5qn">
                    3
                  </span>
                </div>
                <div data-id="element-2031" data-mp="newlagmfy40q">
                  <p className="font-semibold text-gray-800" data-id="element-2032" data-mp="ndlgcdh9eq81">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2033" data-mp="2z0f3jm5wqlmr">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2034" data-mp="2u3x5f5531lje">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-2035" data-mp="3hspt6ebjwn2x">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2036" data-mp="3dspecskji9vc" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-2037" data-mp="22bpos4pku950">
                <HomeIcon className="w-4 h-4" data-id="element-2038" data-mp="2p2k4ysd668ap" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-2039" data-mp="3cpijvlln586i">
      <div className="relative overflow-hidden bg-gradient-to-r from-[#1B365D] via-[#102442] to-[#8B1A1A] py-16 md:py-24" data-id="element-2040" data-mp="3cogx4jv9ix8y">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2041" data-mp="37ss4l0hnugm5">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-2042" data-mp="32y4eix70de5g">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-2043" data-mp="3qo8fsxwq51ta">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-2044" data-mp="6u50faw9mafx" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2045" data-mp="bsyasug99lbv">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-2046" data-mp="prfwkmmlonr5">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-2047" data-mp="14g2aql0hr9gs">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-2048" data-mp="9u27a4p9ec66">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2049" data-mp="et5b5lti6cck">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-2050" data-mp="nu37rj1m3hl4">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-2051" data-mp="stg2epyq5sy9">
            <div className="flex items-start gap-4" data-id="element-2052" data-mp="sseh7snzvq2t">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2053" data-mp="2plq0bp15jkjp">
                <span className="text-sm font-bold text-navy" data-id="element-2054" data-mp="2ulccrw95owrg">1</span>
              </div>
              <div data-id="element-2055" data-mp="2n10e1ziz73ol">
                <p className="font-semibold text-gray-800" data-id="element-2056" data-mp="2n1jxxls6s259">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-2057" data-mp="2i8qr8sd5qjqj">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2058" data-mp="35zminodpbcx9">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2059" data-mp="35z4apuhofa9i">
                <span className="text-sm font-bold text-navy" data-id="element-2060" data-mp="30ygkvc56ozgv">2</span>
              </div>
              <div data-id="element-2061" data-mp="2vzlthlt5i2l5">
                <p className="font-semibold text-gray-800" data-id="element-2062" data-mp="2w39pcrm2j131">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-2063" data-mp="1km1lq8j8xozx">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2064" data-mp="bp302fnm234z">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2065" data-mp="bojulyf5d77u">
                <span className="text-sm font-bold text-navy" data-id="element-2066" data-mp="3txc03w8w0b91">3</span>
              </div>
              <div data-id="element-2067" data-mp="2qre4qxovtiv">
                <p className="font-semibold text-gray-800" data-id="element-2068" data-mp="3k568woqglxuw">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-2069" data-mp="3owek9qbcntap">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2070" data-mp="3g3t5smer48ro">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-2071" data-mp="317bu1u2ngs4l">
              <HomeIcon className="w-4 h-4" data-id="element-2072" data-mp="2mkmx9zzs8t1w" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-2073" data-mp="2ij1ninijouvx">
              <SearchIcon className="w-4 h-4" data-id="element-2074" data-mp="2rh3ialboqrar" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
    </main>;
}